/**
 * Wiederverwendbare Editor-Felder für den Admin-Section-Editor (Phase 2b).
 * Alle Felder sind kontrolliert (value + onChange), keine internen States.
 * Kein Rich-Text-Framework: TextareaField reicht für alle Body-Blöcke.
 */
import { useEffect, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, Film, Play, Eye, EyeOff } from "lucide-react";
import { resolveMediaUrl, type MediaRef } from "@/lib/media";
import AiFlagToggle from "@/components/admin/AiFlagToggle";

/**
 * Ermittelt Kategorie eines MediaField-Kontexts anhand mediaType-Prop.
 * "photo" wenn NUR Foto-Typen erlaubt, "video" wenn NUR Video, sonst "media".
 */
function categoryFor(mediaType?: string | string[]): "photo" | "video" | "media" {
    if (!mediaType) return "media";
    const list = Array.isArray(mediaType) ? mediaType : [mediaType];
    const photos = list.every((t) => t === "photo" || t === "photo_360");
    const videos = list.every((t) => t === "video" || t === "video_360");
    if (photos && !videos) return "photo";
    if (videos && !photos) return "video";
    return "media";
}
const LABELS = {
    photo: { pick: "Bild wechseln", empty: "Bild auswählen", none: "Kein Bild ausgewählt" },
    video: { pick: "Video wechseln", empty: "Video auswählen", none: "Kein Video ausgewählt" },
    media: { pick: "Medium wechseln", empty: "Medium auswählen", none: "Kein Medium ausgewählt" },
} as const;

/* --------------- TextField --------------- */
export function TextField({
    label, value, onChange, testId, placeholder, helpText, maxLength,
}: {
    label: string; value: string; onChange: (v: string) => void;
    testId?: string; placeholder?: string; helpText?: string; maxLength?: number;
}) {
    return (
        <label className="block">
            <span className="hf-eyebrow block mb-1.5" style={{ fontSize: 10 }}>{label}</span>
            <input
                type="text"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                data-testid={testId}
                className="w-full px-3 py-2 rounded-sm border text-sm bg-white"
                style={{ borderColor: "hsl(var(--hf-line))" }}
            />
            {helpText && <span className="mt-1 block text-[11px] text-hf-muted">{helpText}</span>}
        </label>
    );
}

/* --------------- TextareaField --------------- */
export function TextareaField({
    label, value, onChange, testId, rows = 4, helpText, placeholder,
}: {
    label: string; value: string; onChange: (v: string) => void;
    testId?: string; rows?: number; helpText?: string; placeholder?: string;
}) {
    return (
        <label className="block">
            <span className="hf-eyebrow block mb-1.5" style={{ fontSize: 10 }}>{label}</span>
            <textarea
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={rows}
                data-testid={testId}
                className="w-full px-3 py-2 rounded-sm border text-sm font-normal bg-white leading-relaxed"
                style={{ borderColor: "hsl(var(--hf-line))" }}
            />
            {helpText && <span className="mt-1 block text-[11px] text-hf-muted">{helpText}</span>}
        </label>
    );
}

/* --------------- ToggleField --------------- */
export function ToggleField({
    label, value, onChange, testId, helpText,
}: {
    label: string; value: boolean; onChange: (v: boolean) => void;
    testId?: string; helpText?: string;
}) {
    return (
        <div className="flex items-start gap-3">
            <button
                type="button"
                role="switch"
                aria-checked={value}
                onClick={() => onChange(!value)}
                data-testid={testId}
                className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
                style={{ background: value ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))" }}
            >
                <span
                    className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                    style={{ transform: value ? "translateX(24px)" : "translateX(4px)" }}
                />
            </button>
            <div className="flex-1">
                <div className="text-sm text-hf-ink">{label}</div>
                {helpText && <div className="text-[11px] text-hf-muted">{helpText}</div>}
            </div>
        </div>
    );
}

/* --------------- SelectField --------------- */
export function SelectField<T extends string>({
    label, value, onChange, options, testId,
}: {
    label: string; value: T; onChange: (v: T) => void;
    options: { value: T; label: string }[]; testId?: string;
}) {
    return (
        <label className="block">
            <span className="hf-eyebrow block mb-1.5" style={{ fontSize: 10 }}>{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value as T)}
                data-testid={testId}
                className="w-full px-3 py-2 rounded-sm border text-sm bg-white"
                style={{ borderColor: "hsl(var(--hf-line))" }}
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}

function allowedTypesLabel(mediaType?: string | string[]): string | null {
    if (!mediaType) return "Zulässig: Foto, 360°-Foto, Video, 360°-Video";
    const list = Array.isArray(mediaType) ? mediaType : [mediaType];
    const names: Record<string, string> = {
        photo: "Foto",
        photo_360: "360°-Foto",
        video: "Video",
        video_360: "360°-Video",
    };
    const parts = list.map((t) => names[t] || t).filter(Boolean);
    if (parts.length === 0) return null;
    return `Zulässig: ${parts.join(", ")}`;
}

/* --------------- MediaField --------------- */
export function MediaField({
    label, mediaRef, onChange, testId, openPicker, mediaType, helpText, staticFallbackUrl,
}: {
    label: string;
    mediaRef: MediaRef;
    onChange: (v: MediaRef) => void;
    openPicker: (currentType?: string | string[], onPick?: (mediaId: string, path: string) => void) => void;
    testId?: string;
    /** MediaType-Restriktion: einzelner Typ, Liste erlaubter Typen oder undefined (alle Typen). */
    mediaType?: string | string[];
    helpText?: string;
    /**
     * Statische Fallback-URL (aus dem Component-Import), die auf der Live-Seite gerendert
     * wird, wenn kein Library-Medium gesetzt ist. Wird hier als Preview angezeigt, damit
     * der Editor exakt zeigt was live sichtbar ist.
     */
    staticFallbackUrl?: string;
}) {
    const cat = categoryFor(mediaType);
    const L = LABELS[cat];
    const typesInfo = allowedTypesLabel(mediaType);

    const url = resolveMediaUrl(mediaRef, "");
    const path = typeof mediaRef === "object" && mediaRef ? mediaRef.path : (typeof mediaRef === "string" ? mediaRef : "");
    const mediaId = typeof mediaRef === "object" && mediaRef ? mediaRef.media_id : "";

    // Lookup echter media_type + poster über /api/media/{id}/info (für Videos)
    const [info, setInfo] = useState<{ media_type?: string; poster_url?: string; title?: string } | null>(null);
    useEffect(() => {
        if (!mediaId) { setInfo(null); return; }
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/${mediaId}/info`);
                if (!cancelled && res.ok) setInfo(await res.json());
            } catch { /* offline / 404 → ignore */ }
        })();
        return () => { cancelled = true; };
    }, [mediaId]);

    const isVideo = info?.media_type === "video" || info?.media_type === "video_360";
    const typeBadge = info?.media_type === "video_360" ? "360° VIDEO"
        : info?.media_type === "video" ? "VIDEO"
        : info?.media_type === "photo_360" ? "360° FOTO"
        : null;
    // Thumbnail: bei Videos Poster nutzen (falls vorhanden), sonst /api/media/{id} als Redirect.
    const thumbUrl = isVideo ? (info?.poster_url || "") : url;

    const handlePick = () => {
        openPicker(mediaType, (id: string, p: string) => {
            onChange({ path: p, media_id: id });
        });
    };
    const handleRemove = () => onChange(null);

    return (
        <div>
            <div className="hf-eyebrow mb-1" style={{ fontSize: 10 }}>{label}</div>
            {typesInfo && (
                <div className="text-[10px] text-hf-muted mb-2 tracking-wide">{typesInfo}</div>
            )}
            {url ? (
                <div
                    className="flex gap-3 p-3 rounded-sm bg-white items-center"
                    style={{ border: "1px solid hsl(var(--hf-line))" }}
                >
                    <div className="relative w-[120px] h-[80px] shrink-0 rounded-sm overflow-hidden" style={{ background: "hsl(var(--hf-ink))" }}>
                        {thumbUrl ? (
                            <img
                                src={thumbUrl}
                                alt=""
                                className="w-full h-full object-cover"
                            />
                        ) : isVideo ? (
                            <div className="w-full h-full flex items-center justify-center text-hf-gold-soft">
                                <Film size={24} />
                            </div>
                        ) : (
                            <div className="w-full h-full" style={{ background: "hsl(var(--hf-surface))" }} />
                        )}
                        {isVideo && (
                            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <Play size={22} className="text-white drop-shadow-lg" />
                            </span>
                        )}
                        {typeBadge && (
                            <span
                                className="absolute top-1 left-1 text-[8px] tracking-[0.14em] uppercase px-1 py-0.5 rounded-sm"
                                style={{ background: "hsl(var(--hf-ink) / 0.8)", color: "hsl(var(--hf-gold-soft))" }}
                            >
                                {typeBadge}
                            </span>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm text-hf-ink truncate" title={path}>
                            {info?.title || path || "(kein Pfad)"}
                        </div>
                        {mediaId && (
                            <div className="text-[11px] text-hf-muted font-mono truncate">
                                id: {mediaId}
                            </div>
                        )}
                        <div className="mt-2 flex gap-2">
                            <button
                                type="button"
                                onClick={handlePick}
                                data-testid={testId ? `${testId}-pick` : undefined}
                                className="text-xs px-3 py-1.5 rounded-sm border transition-colors"
                                style={{
                                    borderColor: "hsl(var(--hf-line))",
                                    background: "hsl(var(--hf-surface))",
                                }}
                            >
                                {L.pick}
                            </button>
                            <button
                                type="button"
                                onClick={handleRemove}
                                data-testid={testId ? `${testId}-remove` : undefined}
                                className="text-xs px-3 py-1.5 rounded-sm border transition-colors text-hf-danger"
                                style={{ borderColor: "hsl(var(--hf-line))" }}
                            >
                                Entfernen
                            </button>
                        </div>
                        {mediaId && (
                            <div className="mt-2">
                                <AiFlagToggle
                                    mediaId={mediaId}
                                    compact
                                    showAutoSaveHint
                                    testIdPrefix={testId ? `${testId}-ai-flag` : "media-field-ai-flag"}
                                />
                            </div>
                        )}
                    </div>
                </div>
            ) : staticFallbackUrl ? (
                <div
                    className="flex gap-3 p-3 rounded-sm bg-white items-center"
                    style={{ border: "1px dashed hsl(var(--hf-line))" }}
                    data-testid={testId ? `${testId}-fallback-preview` : undefined}
                >
                    <div className="relative w-[120px] h-[80px] shrink-0 rounded-sm overflow-hidden" style={{ background: "hsl(var(--hf-surface))" }}>
                        <img src={staticFallbackUrl} alt="" className="w-full h-full object-cover" />
                        <span
                            className="absolute top-1 left-1 text-[8px] tracking-[0.14em] uppercase px-1 py-0.5 rounded-sm"
                            style={{ background: "hsl(var(--hf-ink) / 0.8)", color: "hsl(var(--hf-gold-soft))" }}
                        >
                            STANDARD
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm text-hf-ink truncate italic">Standard-Bild aus Vorlage</div>
                        <div className="text-[11px] text-hf-muted mt-0.5">
                            Wird auf der Live-Seite gerendert, solange kein Library-Medium ausgewählt ist.
                        </div>
                        <div className="mt-2 flex gap-2">
                            <button
                                type="button"
                                onClick={handlePick}
                                data-testid={testId ? `${testId}-pick` : undefined}
                                className="text-xs px-3 py-1.5 rounded-sm border transition-colors"
                                style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-surface))" }}
                            >
                                {L.pick}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={handlePick}
                    data-testid={testId ? `${testId}-pick` : undefined}
                    className="w-full h-[100px] rounded-sm flex flex-col items-center justify-center gap-2 text-sm text-hf-muted transition-colors hover:bg-hf-surface"
                    style={{
                        border: "1.5px dashed hsl(var(--hf-line))",
                        background: "hsl(var(--hf-bg))",
                    }}
                >
                    {cat === "video" ? <Film size={22} /> : <ImageIcon size={22} />}
                    <span>{L.empty}</span>
                </button>
            )}
            {helpText && <p className="mt-1 text-[11px] text-hf-muted">{helpText}</p>}
        </div>
    );
}

/* --------------- RepeaterField --------------- */
export function RepeaterField<T>({
    label, items, onChange, renderItem, newItem, minItems, maxItems, testId,
}: {
    label: string;
    items: T[];
    onChange: (items: T[]) => void;
    renderItem: (item: T, idx: number, update: (item: T) => void) => React.ReactNode;
    newItem: () => T;
    minItems?: number; maxItems?: number;
    testId?: string;
}) {
    const move = (from: number, to: number) => {
        if (to < 0 || to >= items.length) return;
        const next = [...items];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        onChange(next);
    };
    const remove = (idx: number) => {
        if (minItems !== undefined && items.length <= minItems) return;
        const next = [...items];
        next.splice(idx, 1);
        onChange(next);
    };
    const add = () => {
        if (maxItems !== undefined && items.length >= maxItems) return;
        onChange([...items, newItem()]);
    };

    return (
        <div data-testid={testId}>
            <div className="hf-eyebrow mb-2 flex items-baseline justify-between" style={{ fontSize: 10 }}>
                <span>{label}</span>
                <span className="text-hf-muted normal-case tracking-normal" style={{ fontSize: 10, letterSpacing: 0 }}>
                    {items.length} {items.length === 1 ? "Eintrag" : "Einträge"}
                    {items.filter((x) => (x as unknown as { enabled?: boolean })?.enabled === false).length > 0 && (
                        <> · {items.filter((x) => (x as unknown as { enabled?: boolean })?.enabled === false).length} ausgeblendet</>
                    )}
                </span>
            </div>
            <div className="space-y-3">
                {items.map((it, idx) => {
                    const enabled = (it as unknown as { enabled?: boolean })?.enabled !== false;
                    const toggleEnabled = () => {
                        const next = [...items];
                        next[idx] = { ...(next[idx] as object), enabled: !enabled } as T;
                        onChange(next);
                    };
                    return (
                        <div
                            key={idx}
                            className="p-4 rounded-sm bg-white relative"
                            style={{
                                border: "1px solid hsl(var(--hf-line))",
                                opacity: enabled ? 1 : 0.55,
                            }}
                            data-testid={testId ? `${testId}-item-${idx}` : undefined}
                        >
                            {!enabled && (
                                <span
                                    className="absolute -top-2 left-4 text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-sm"
                                    style={{ background: "hsl(var(--hf-ink))", color: "hsl(var(--hf-bg))" }}
                                >
                                    AUSGEBLENDET
                                </span>
                            )}
                            <div className="flex items-center justify-between mb-3 text-[11px] text-hf-muted">
                                <span>#{idx + 1}</span>
                                <div className="flex gap-1 items-center">
                                    <button
                                        type="button"
                                        aria-label={enabled ? "Ausblenden" : "Sichtbar machen"}
                                        onClick={toggleEnabled}
                                        title={enabled ? "Auf der Live-Seite ausblenden" : "Wieder anzeigen"}
                                        className="p-1 rounded-sm hover:bg-hf-surface transition-colors"
                                        style={{ color: enabled ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-muted))" }}
                                        data-testid={testId ? `${testId}-item-${idx}-toggle-enabled` : undefined}
                                    >
                                        {enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                                    </button>
                                    <button
                                        type="button"
                                        aria-label="Nach oben"
                                        onClick={() => move(idx, idx - 1)}
                                        disabled={idx === 0}
                                        className="p-1 rounded-sm disabled:opacity-30 hover:bg-hf-surface"
                                        data-testid={testId ? `${testId}-item-${idx}-up` : undefined}
                                    >
                                        <ChevronUp size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label="Nach unten"
                                        onClick={() => move(idx, idx + 1)}
                                        disabled={idx === items.length - 1}
                                        className="p-1 rounded-sm disabled:opacity-30 hover:bg-hf-surface"
                                        data-testid={testId ? `${testId}-item-${idx}-down` : undefined}
                                    >
                                        <ChevronDown size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label="Löschen"
                                        onClick={() => remove(idx)}
                                        disabled={minItems !== undefined && items.length <= minItems}
                                        className="p-1 rounded-sm disabled:opacity-30 text-hf-danger hover:bg-hf-surface"
                                        data-testid={testId ? `${testId}-item-${idx}-delete` : undefined}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                            {renderItem(it, idx, (next) => {
                                const arr = [...items];
                                arr[idx] = next;
                                onChange(arr);
                            })}
                        </div>
                    );
                })}
            </div>
            <button
                type="button"
                onClick={add}
                disabled={maxItems !== undefined && items.length >= maxItems}
                className="mt-3 inline-flex items-center gap-2 text-sm px-3 py-2 rounded-sm border disabled:opacity-40"
                style={{
                    borderColor: "hsl(var(--hf-line))",
                    background: "hsl(var(--hf-surface))",
                }}
                data-testid={testId ? `${testId}-add` : undefined}
            >
                <Plus size={14} /> Neu hinzufügen
            </button>
        </div>
    );
}

/* --------------- MediaListField (schlicht, ohne dnd-kit) --------------- */

/**
 * Erweiterter Item-Typ für MediaListField mit optionalem Sichtbarkeits-Flag.
 * `enabled=false` blendet die Kachel im Frontend aus (via `isEnabled`).
 */
export interface MediaListEntry {
    ref: MediaRef;
    enabled?: boolean;
}

export function MediaListField({
    label, items, onChange, openPicker, mediaType, testId, helpText,
    withVisibility = false,
}: {
    label: string;
    items: MediaListEntry[];
    onChange: (items: MediaListEntry[]) => void;
    openPicker: (currentType?: string | string[], onPick?: (mediaId: string, path: string) => void) => void;
    mediaType?: string | string[];
    testId?: string;
    helpText?: string;
    /** Wenn true, rendert pro Kachel einen Sichtbar/Verborgen-Toggle. */
    withVisibility?: boolean;
}) {
    const remove = (idx: number) => {
        const next = [...items];
        next.splice(idx, 1);
        onChange(next);
    };
    const move = (from: number, to: number) => {
        if (to < 0 || to >= items.length) return;
        const next = [...items];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        onChange(next);
    };
    const toggle = (idx: number) => {
        const next = items.map((it, i) =>
            i === idx ? { ...it, enabled: it.enabled === false ? true : false } : it,
        );
        onChange(next);
    };
    const add = () => {
        openPicker(mediaType, (id: string, p: string) => {
            onChange([...items, { ref: { path: p, media_id: id }, enabled: true }]);
        });
    };

    return (
        <div data-testid={testId}>
            <div className="hf-eyebrow mb-2" style={{ fontSize: 10 }}>{label}</div>
            {helpText && (
                <p
                    className="mb-2 text-[11px]"
                    style={{ color: "hsl(var(--hf-muted))" }}
                    data-testid={testId ? `${testId}-help` : undefined}
                >
                    {helpText}
                </p>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {items.map((entry, idx) => (
                    <MediaListThumbnail
                        key={idx}
                        ref_={entry.ref}
                        idx={idx}
                        enabled={entry.enabled !== false}
                        showToggle={withVisibility}
                        onToggle={() => toggle(idx)}
                        onMoveLeft={() => move(idx, idx - 1)}
                        onMoveRight={() => move(idx, idx + 1)}
                        isFirst={idx === 0}
                        isLast={idx === items.length - 1}
                        onRemove={() => remove(idx)}
                        testId={testId}
                    />
                ))}
                <button
                    type="button"
                    onClick={add}
                    className="aspect-[3/2] rounded-sm flex items-center justify-center text-hf-muted hover:bg-hf-surface"
                    style={{ border: "1.5px dashed hsl(var(--hf-line))" }}
                    data-testid={testId ? `${testId}-add` : undefined}
                >
                    <Plus size={20} />
                </button>
            </div>
        </div>
    );
}

/** Einzelne Kachel im MediaListField — mit Type-Aware Thumbnail und
 *  optionalem Sichtbarkeits-Toggle. */
function MediaListThumbnail({
    ref_, idx, enabled, showToggle, onToggle,
    onMoveLeft, onMoveRight, isFirst, isLast, onRemove, testId,
}: {
    ref_: MediaRef; idx: number;
    enabled: boolean; showToggle: boolean; onToggle?: () => void;
    onMoveLeft: () => void; onMoveRight: () => void;
    isFirst: boolean; isLast: boolean;
    onRemove: () => void;
    testId?: string;
}) {
    const url = resolveMediaUrl(ref_, "");
    const mediaId = typeof ref_ === "object" && ref_ ? ref_.media_id : "";
    const [info, setInfo] = useState<{ media_type?: string; poster_url?: string } | null>(null);
    useEffect(() => {
        if (!mediaId) { setInfo(null); return; }
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/${mediaId}/info`);
                if (!cancelled && res.ok) setInfo(await res.json());
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [mediaId]);

    const mType = info?.media_type || "photo";
    const isVideo = mType === "video" || mType === "video_360";
    const badge =
        mType === "video_360" ? "360° VIDEO"
        : mType === "video" ? "VIDEO"
        : mType === "photo_360" ? "360° FOTO"
        : "FOTO";
    // Bei Videos Poster zeigen. Wenn kein Poster → grafischer Fallback.
    const thumbUrl = isVideo ? info?.poster_url || "" : url;

    return (
        <div
            className="relative group rounded-sm overflow-hidden"
            style={{
                border: "1px solid hsl(var(--hf-line))",
                background: "hsl(var(--hf-ink))",
                opacity: enabled ? 1 : 0.4,
                filter: enabled ? undefined : "grayscale(0.5)",
                transition: "opacity 0.15s, filter 0.15s",
            }}
            data-testid={testId ? `${testId}-item-${idx}` : undefined}
            data-enabled={enabled ? "true" : "false"}
        >
            <div className="w-full aspect-[3/2] relative" style={{ background: "hsl(var(--hf-surface))" }}>
                {thumbUrl ? (
                    <img
                        src={thumbUrl}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                ) : isVideo ? (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: "hsl(var(--hf-ink))", color: "hsl(var(--hf-gold-soft))" }}>
                        <Film size={22} />
                    </div>
                ) : (
                    <div className="w-full h-full" style={{ background: "hsl(var(--hf-surface))" }} />
                )}
                {isVideo && (
                    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Play size={22} className="text-white drop-shadow-lg" />
                    </span>
                )}
                {/* Type-Badge oben rechts */}
                <span
                    className="absolute top-1 right-1 text-[8px] tracking-[0.14em] uppercase px-1 py-0.5 rounded-sm"
                    style={{ background: "hsl(var(--hf-ink) / 0.82)", color: "hsl(var(--hf-gold-soft))" }}
                >
                    {badge}
                </span>
                {/* KI-Toggle oben links (immer sichtbar, kein Hover nötig).
                 *  Dank Pill-Design mit „KI"-Text beim Aktiv-Zustand sofort
                 *  erkennbar. Overlay-Position kollidiert nicht mit dem
                 *  Type-Badge (rechts) und der Action-Bar (unten). */}
                {mediaId && (
                    <span className="absolute top-1 left-1 z-10">
                        <AiFlagToggle
                            mediaId={mediaId}
                            variant="icon"
                            testIdPrefix={testId ? `${testId}-ai-flag-${idx}` : `media-list-ai-flag-${idx}`}
                        />
                    </span>
                )}
                {/* Verborgen-Label wenn disabled — direkt unter dem KI-Toggle */}
                {!enabled && (
                    <span
                        className="absolute top-8 left-1 text-[9px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-sm font-medium"
                        style={{ background: "hsl(var(--hf-ink) / 0.9)", color: "#faf8f3" }}
                    >
                        VERBORGEN
                    </span>
                )}
            </div>
            {/* Action-Bar (Reorder + Toggle + Remove) */}
            <div
                className="absolute inset-x-0 bottom-0 flex justify-between items-center px-1.5 py-1 bg-hf-ink/80 opacity-100"
                style={{ backdropFilter: "blur(3px)" }}
            >
                <div className="flex gap-0.5">
                    <button
                        type="button"
                        onClick={onMoveLeft}
                        disabled={isFirst}
                        className="p-0.5 text-white disabled:opacity-30 hover:text-hf-gold-soft"
                        aria-label="Nach links"
                    >
                        <ChevronUp size={12} className="-rotate-90" />
                    </button>
                    <button
                        type="button"
                        onClick={onMoveRight}
                        disabled={isLast}
                        className="p-0.5 text-white disabled:opacity-30 hover:text-hf-gold-soft"
                        aria-label="Nach rechts"
                    >
                        <ChevronDown size={12} className="-rotate-90" />
                    </button>
                </div>
                {showToggle && (
                    <button
                        type="button"
                        onClick={onToggle}
                        className="text-[9px] px-2 py-0.5 rounded-full tracking-[0.14em] uppercase transition-colors"
                        style={{
                            background: enabled ? "hsl(var(--hf-gold) / 0.9)" : "hsl(var(--hf-line))",
                            color: enabled ? "#0e1726" : "#faf8f3",
                            border: `1px solid ${enabled ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-muted))"}`,
                        }}
                        title={enabled ? "Ausblenden" : "Wieder einblenden"}
                        data-testid={testId ? `${testId}-toggle-${idx}` : undefined}
                    >
                        {enabled ? "Sichtbar" : "Verborgen"}
                    </button>
                )}
                <button
                    type="button"
                    onClick={onRemove}
                    className="p-0.5 text-white hover:text-hf-gold-soft"
                    aria-label="Entfernen"
                >
                    <Trash2 size={12} />
                </button>
            </div>
        </div>
    );
}

/* --------------- SectionCard --------------- */
export function SectionCard({
    title, description, sectionKey, anchor, children, defaultOpen = true, dirty = false,
}: {
    title: string; description?: string; sectionKey: string; anchor?: string;
    children: React.ReactNode; defaultOpen?: boolean; dirty?: boolean;
}) {
    // Zustand pro Session merken
    const storageKey = `hf-editor-open-${sectionKey}`;
    const [open, setOpen] = useState<boolean>(() => {
        if (typeof window === "undefined") return defaultOpen;
        const s = window.sessionStorage.getItem(storageKey);
        return s === null ? defaultOpen : s === "1";
    });
    useEffect(() => {
        if (typeof window !== "undefined") {
            window.sessionStorage.setItem(storageKey, open ? "1" : "0");
        }
    }, [open, storageKey]);

    return (
        <section
            className="rounded-md"
            style={{
                background: "hsl(var(--hf-surface))",
                border: "1px solid hsl(var(--hf-line))",
                boxShadow: "var(--shadow-warm-sm)",
            }}
            data-testid={`editor-section-${sectionKey}`}
        >
            <header className="flex items-start justify-between p-6 md:p-7">
                <div className="min-w-0">
                    <h2 className="font-display text-2xl md:text-3xl leading-tight flex items-center gap-3">
                        {title}
                        {dirty && (
                            <span
                                className="text-[10px] tracking-[0.16em] uppercase px-2 py-0.5 rounded-sm"
                                style={{
                                    background: "hsl(var(--hf-gold-soft))",
                                    color: "hsl(var(--hf-gold-dark))",
                                }}
                            >
                                Geändert
                            </span>
                        )}
                    </h2>
                    {description && (
                        <p className="mt-1 text-sm text-hf-muted">{description}</p>
                    )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {anchor && (
                        <a
                            href={`/#${anchor}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] tracking-[0.14em] uppercase text-hf-muted hover:text-hf-gold-dark"
                        >
                            Auf Live-Seite ansehen ↗
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        aria-label={open ? "Zuklappen" : "Aufklappen"}
                        data-testid={`editor-section-${sectionKey}-toggle`}
                        className="p-2 rounded-sm hover:bg-hf-bg"
                    >
                        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                </div>
            </header>
            {open && (
                <div className="p-6 md:p-7 pt-0 md:pt-0 space-y-5">
                    {children}
                </div>
            )}
        </section>
    );
}
